import React, { useState, useMemo, useEffect } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Stack,
} from '@mui/material';
import { getUser } from '@/lib/common';
import { Loader } from '@/components/loader/loader';
import { useGlobalBudget } from '@/screens/budgets/components/hooks/useGlobalBudget';
import { AppCard } from '@/components/app-card/app-card';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import AnnualBudgetChart from '../charts/annual-budget-chart';

import { useDepartments, Department } from './hooks/useDepartments';
import { useBudgetChanges } from './hooks/useBudgetChanges';
import { DepartmentsTable } from './departments/departments-table';
import { GlobalBudgetAllocation } from './global/global-budget-allocation';
import { CreateDepartmentModal } from './departments/create-department-modal';
import { GenericBudgetModal } from './global/generic-budget-modal';
import {
  getEffectiveBudgetValue,
  getEffectiveBudgetCurrency,
  validateBudgetSum,
} from '../calculations';
import { BudgetFormData } from '../types';
import { BudgetChangeModal, BudgetChangeData } from './budget-change-modal';
import { PermissionArea } from '@cloudcost/shared-types/users';
import { hasUserAccessTo, refreshUserRoles } from '@/lib/permissions';

interface DepartmentFormData {
  id?: number;
  name: string;
  id_owner: number | null;
}

export default function GlobalBudgetSplit() {
  const [alertOwner, setAlertOwner] = useState(false);
  const [open, setOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [systemsWarningOpen, setSystemsWarningOpen] = useState(false);
  const [userValue, setUserValue] = useState<DepartmentFormData>({
    name: '',
    id_owner: null,
  });
  const [editValue, setEditValue] = useState<DepartmentFormData>({
    name: '',
    id_owner: null,
  });
  const [budgetValue, setBudgetValue] = useState<BudgetFormData>({
    id: 0,
    name: '',
    original_currency_value: '',
    original_currency: null,
  });
  const [validationErrorOpen, setValidationErrorOpen] = useState(false);
  const [budgetChangeModalOpen, setBudgetChangeModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);
  const [sponsorId, setSponsorId] = useState<number>();

  const user = getUser();

  const { globalBudget, loading: globalBudgetLoading } = useGlobalBudget();
  const { departmentsList, users, currencies, loading, refreshData, setDepartmentsList } =
    useDepartments();

  useEffect(() => {
    if (users.length > 0) {
      const sponsor = users.find((user) => user.is_sponsor);
      if (sponsor) {
        setSponsorId(sponsor.id);
      }
    }
  }, [users]);

  // Update budgetValue currency when globalBudget becomes available
  useEffect(() => {
    if (
      globalBudget?.original_currency &&
      budgetValue.original_currency === null
    ) {
      setBudgetValue((prev) => ({
        ...prev,
        original_currency: globalBudget.original_currency,
      }));
    }
  }, [globalBudget, budgetValue.original_currency]);

  const {
    pendingBudgetChanges,
    bulkSaveLoading,
    addPendingChange,
    clearPendingChanges,
    bulkSaveBudgets,
  } = useBudgetChanges({
    bulkSaveUrl: urls.DevOps.bulkSaveDepartmentBudgets,
  });

  const canViewAllDepartments = hasUserAccessTo(
    PermissionArea.DEPARTMENT_BUDGET_VIEW_ALL,
  );

  const visibleDepartmentsList = useMemo(() => {
    if (canViewAllDepartments) {
      return departmentsList; // Admin/Sponsor can see all departments
    }
    // Regular users can only see departments they own
    return departmentsList.filter((department) => {
      return department.id_owner === user?.id;
    });
  }, [departmentsList, user, canViewAllDepartments]);

  const canCreateDepartment = user?.is_admin || user?.is_sponsor;
  const canSetBudget = user?.is_sponsor || user?.is_admin;
  const canEditDepartment = user?.is_admin || user?.is_sponsor;

  const handleSetBudget = (department: Department) => {
    const effectiveValue = getEffectiveBudgetValue(
      department,
      pendingBudgetChanges,
    );
    const effectiveCurrency = getEffectiveBudgetCurrency(
      department,
      pendingBudgetChanges,
    );

    // If there's an existing saved budget and no pending changes, open change request modal
    if (department.value && !pendingBudgetChanges.has(department.id)) {
      setSelectedDepartment(department);
      setBudgetChangeModalOpen(true);
    } else {
      setBudgetValue({
        id: department.id,
        name: department.name,
        original_currency_value: effectiveValue || '',
        original_currency:
          effectiveCurrency || globalBudget?.original_currency || null,
        budget_id: department.budget_id,
      });
      setBudgetModalOpen(true);
    }
  };

  const handleEditDepartment = (department: Department) => {
    setEditValue({
      id: department.id,
      name: department.name,
      id_owner: department.id_owner,
    });
    setEditModalOpen(true);
  };

  const handleOpen2 = () => {
    setUserValue({ name: '', id_owner: null });
    setOpen(true);
  };

  const deleteDepartment = (e: any) => {
    if (!editValue.id) {
      return;
    }
    // Check if there are systems assigned to this department
    fetchWrapper
      .get(urls.DevOps.allsystems)
      .then((data) => {
        const systemsInDepartment = data.filter(
          (system: any) => system.department_id === editValue.id,
        );
        if (systemsInDepartment.length > 0) {
          // Show warning dialog if systems exist
          setSystemsWarningOpen(true);
        } else {
          setConfirmation(true);
          closeConfirmation();
          if (editValue.id) {
            fetchWrapper
              .delete(urls.DevOps.deleteDepartment, {
                id: editValue.id,
              })
              .then(() => {
                fetchWrapper
                  .get(urls.DevOps.getDepartments)
                  .then((data) => setDepartmentsList(data))
                  .catch((error) => console.error('There was an error!', error));

                refreshData();
                handleClose();
                refreshUserRoles();               
              })
              .catch((error) => console.error('There was an error!', error));
          }
        }
      })
      .catch((error) => {
        console.error('Error checking systems:', error);
        setConfirmation(true);
      });
  };
  

  const handleBudgetChangeSubmit = async (data: BudgetChangeData) => {
    if (!selectedDepartment) return;

    try {
      await fetchWrapper.post(urls.DevOps.createDepartmentBudgetChangeRequest, {
        departmentId: selectedDepartment.id,
        currency: selectedDepartment.original_currency || 'USD',
        newBudgetValue: data.newAnnualBudget,
        reason: data.reason,
        requesterId: user?.id,
      });

      refreshData();
      setBudgetChangeModalOpen(false);
      setSelectedDepartment(null);
    } catch (error) {
      console.error('Error processing department budget change:', error);
    }
  };
  const openConfirmation = () => {
    setConfirmation(true);
  };

  const closeConfirmation = () => {
    setConfirmation(false);
  };

  const handleClose = () => {
    setAlertOwner(false);
    setOpen(false);
    setEditModalOpen(false);
    setBudgetModalOpen(false);
    setBudgetChangeModalOpen(false);
    setSelectedDepartment(null);
    setSystemsWarningOpen(false);
    setConfirmation(false);
    setUserValue({ name: '', id_owner: null });
    setEditValue({ name: '', id_owner: null });
    setBudgetValue({
      id: 0,
      name: '',
      original_currency_value: '',
      original_currency: globalBudget?.original_currency || null,
    });
  };

  const createDepartment = () => {
    if (!userValue.name) {
      return;
    }
    if (!userValue.id_owner) {
      setAlertOwner(true);
      return;
    }

    fetchWrapper
      .post(urls.DevOps.createDepartment, {
        name: userValue.name,
        id_owner: userValue.id_owner,
      })
      .then(() => {
        refreshData();
        handleClose();
        refreshUserRoles();
      });
  };

  const editDepartment = () => {
    if (!editValue.name) {
      return;
    }
    if (!editValue.id_owner) {
      setAlertOwner(true);
      return;
    }

    fetchWrapper
      .post(urls.DevOps.updateDepartment, {
        id: editValue.id,
        name: editValue.name,
        id_owner: editValue.id_owner,
        new: false,
      })
      .then(() => {
        refreshData();
        handleClose();
        refreshUserRoles();
      });
  };

  const setBudget = () => {
    if (
      !budgetValue.original_currency_value ||
      !budgetValue.original_currency
    ) {
      return;
    }

    addPendingChange(budgetValue.id, {
      id: budgetValue.id,
      value: budgetValue.original_currency_value,
      currency: budgetValue.original_currency,
      budget_id: budgetValue.budget_id,
    });

    handleClose();
  };

  const handleBulkSave = () => {
    const validateAll = () =>
      validateBudgetSum(
        visibleDepartmentsList,
        globalBudget?.original_currency_value || 0,
        pendingBudgetChanges,
      );
    const onValidationError = () => setValidationErrorOpen(true);
    const onSuccess = () => refreshData();

    bulkSaveBudgets(validateAll, onValidationError, onSuccess);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Stack spacing={2}>
      {canCreateDepartment && (
        <>
          <GlobalBudgetAllocation
            departmentsList={visibleDepartmentsList}
            globalBudget={globalBudget}
            globalBudgetLoading={globalBudgetLoading}
          />

          <AppCard
            className="card100"
            title="Annual Department Budgets"
            icon={<BarChartOutlinedIcon color="primary" />}>
            <AnnualBudgetChart
              data={visibleDepartmentsList.map((dept) => ({
                name: dept.name,
                budgetValue: dept.value || 0,
              }))}
              referenceLine={{
                value: globalBudget?.original_currency_value ?? 0,
                label: 'Annual Global Budget',
              }}
              seriesLabel="Department Budget"
            />
          </AppCard>

          <DepartmentsTable
            departmentsList={visibleDepartmentsList}
            pendingBudgetChanges={pendingBudgetChanges}
            globalBudgetValue={globalBudget?.original_currency_value || 0}
            globalBudgetCurrency={globalBudget?.original_currency}
            onEditDepartment={handleEditDepartment}
            onSetBudget={handleSetBudget}
            onCreateDepartment={handleOpen2}
            onBulkSave={handleBulkSave}
            onCancelChanges={clearPendingChanges}
            bulkSaveLoading={bulkSaveLoading}
            canCreateDepartment={canCreateDepartment ?? false}
            canEditDepartment={canEditDepartment ?? false}
            canSetBudget={canSetBudget ?? false}
          />
        </>
      )}

      <CreateDepartmentModal
        open={open}
        onClose={handleClose}
        onSubmit={createDepartment}
        onDelete={openConfirmation}
        users={users}
        formData={userValue}
        setFormData={setUserValue}
        alertOwner={alertOwner}
      />

      <CreateDepartmentModal
        open={editModalOpen}
        onClose={handleClose}
        onSubmit={editDepartment}
        onDelete={openConfirmation}
        users={users}
        formData={editValue}
        setFormData={setEditValue}
        alertOwner={alertOwner}
        isEditMode={true}
      />

      <GenericBudgetModal
        open={budgetModalOpen}
        onClose={handleClose}
        onSubmit={setBudget}
        currencies={currencies}
        formData={budgetValue}
        setFormData={setBudgetValue}
      />
    
      <Dialog
        open={validationErrorOpen}
        onClose={() => setValidationErrorOpen(false)}
        aria-labelledby="validation-error-dialog-title"
        aria-describedby="validation-error-dialog-description">
        <DialogTitle id="validation-error-dialog-title">
          Budget Validation Error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="validation-error-dialog-description">
            Sum of allocated Department Budgets must not exceed the Global
            Budget!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationErrorOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <BudgetChangeModal
        open={budgetChangeModalOpen}
        onClose={() => {
          setBudgetChangeModalOpen(false);
          setSelectedDepartment(null);
        }}
        onSubmit={handleBudgetChangeSubmit}
        budgetType="department"
        currentBudget={selectedDepartment?.value || 0}
        budgetCurrency={selectedDepartment?.original_currency || ''}
        entityName={selectedDepartment?.name}
        departmentOwner={selectedDepartment?.username}
        globalBudget={globalBudget?.original_currency_value}
        departmentsBudgetsSum={
          visibleDepartmentsList.reduce(
            (sum, dept) => sum + (dept.value || 0),
            0,
          ) - (selectedDepartment?.value || 0)
        }
        
      />
      <Dialog
        open={systemsWarningOpen}
        onClose={handleClose}
        aria-labelledby="systems-warning-dialog-title"
        aria-describedby="systems-warning-dialog-description">
        <DialogTitle id="systems-warning-dialog-title">
          Systems Assigned to Department
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="systems-warning-dialog-description">
            Before deleting Department, reassign or delete all Systems assigned to this Department
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmation}
        onClose={closeConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete the department?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Deleting the department will also delete budget history for that department
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmation}>Cancel</Button>
          <Button onClick={deleteDepartment} autoFocus data-testid="global-budget-dialog-confirm">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
